import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Avatar, Box, Button, Chip, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState, GlassIconButton, LoadingOverlay, PageHeader, PagePaper,
} from 'dashboard/components/common';
import MemberRoleSelector from './MemberRoleSelector';
import InviteDialog from './InviteDialog';
import { memberInitials, memberDisplayName } from './iamHelpers';

/**
 * Brand-level IAM page.
 *
 * Owns the data fetching (members + invites + roles) and the action
 * handlers. The visual rows are MUI Table + the two extracted
 * sub-components (MemberRoleSelector inline, InviteDialog modal).
 */
export default function IAMComponent() {
  const [selectedBrand] = useOutletContext();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadMembers = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.getAccessList({ brand_id: selectedBrand })
      .then((res) => setMembers(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load members', { toastId: 'iam-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  const loadInvites = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    return API.listIamInvites({ brand_id: selectedBrand })
      .then((res) => setInvites(Array.isArray(res) ? res : []))
      .catch(() => setInvites([]));
  }, [selectedBrand]);

  // Three independent fetches on mount — guard each so a brand-switch
  // mid-flight or a StrictMode double-mount doesn't write stale data.
  useEffect(() => {
    let cancelled = false;
    API.getRoles()
      .then((res) => { if (!cancelled) setRoles(Array.isArray(res) ? res : []); })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.getAccessList({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setMembers(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load members', { toastId: 'iam-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    API.listIamInvites({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setInvites(Array.isArray(res) ? res : []); })
      .catch(() => { if (!cancelled) setInvites([]); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const handleInvite = (email, rolesArr) => {
    setInviting(true);
    API.addUserToAccessList({ email, brand_id: selectedBrand, roles: rolesArr })
      .then((res) => {
        if (res?.invited) toast.success(res.message || `Invitation sent to ${email}`);
        else toast.success(`${email} added`);
        setInviteOpen(false);
        loadMembers();
        loadInvites();
      })
      .catch((err) => toast.error(err?.message || 'Failed to add member'))
      .finally(() => setInviting(false));
  };

  const handleRevokeInvite = (invite) => {
    if (!window.confirm(`Revoke the invite for ${invite.email}?`)) return;
    API.revokeIamInvite({ _id: invite._id })
      .then(() => { toast.success('Invite revoked'); loadInvites(); })
      .catch((err) => toast.error(err?.message || 'Failed to revoke'));
  };

  const handleCopyInviteLink = async (invite) => {
    try {
      await navigator.clipboard.writeText(invite.accept_url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleRoleChange = (member, newRoles) => {
    // Optimistic update — roll back via loadMembers if the API rejects.
    const optimistic = members.map((m) =>
      m._id === member._id ? { ...m, roles: newRoles } : m
    );
    setMembers(optimistic);
    API.updateUserRole({ _id: member._id, roles: newRoles })
      .then(() => toast.success('Roles updated'))
      .catch((err) => {
        toast.error(err?.message || 'Failed to update');
        loadMembers();
      });
  };

  const handleRemove = (member) => {
    if (!window.confirm(`Remove ${memberDisplayName(member)} from this brand?`)) return;
    API.removeUserFromAccessList({ _id: member._id })
      .then(() => { toast.success('Member removed'); loadMembers(); })
      .catch((err) => toast.error(err?.message || 'Failed to remove'));
  };

  const ownerCount = useMemo(
    () => members.filter((m) => (m.roles || []).includes('owner')).length,
    [members]
  );

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Brand members'
          subtitle='Invite teammates and control what they can access. Owner has full control.'
          actions={
            <Button variant='contained' onClick={() => setInviteOpen(true)}>
              + Invite member
            </Button>
          }
        />

        {!selectedBrand && (
          <EmptyState>Select a brand from the navbar to manage its members.</EmptyState>
        )}

        {selectedBrand && (
          <LoadingOverlay loading={loading && members.length === 0} minHeight={160}>
            <TableContainer
              component={Box}
              sx={{
                border: '1px solid', borderColor: 'divider',
                borderRadius: '12px', overflowX: 'auto',
              }}
            >
              <Table size='medium'>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && members.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <EmptyState>No members yet. Invite the first teammate.</EmptyState>
                      </TableCell>
                    </TableRow>
                  )}
                  {members.map((m) => {
                    const isOwner = (m.roles || []).includes('owner');
                    return (
                      <TableRow key={m._id}>
                        <TableCell>
                          <Stack direction='row' spacing='10px' alignItems='center' flexWrap='wrap'>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 13 }}>
                              {memberInitials(m)}
                            </Avatar>
                            <Box>
                              <Typography variant='body2' fontWeight='600'>
                                {memberDisplayName(m)}
                              </Typography>
                              {isOwner && (
                                <Chip size='small' label='OWNER' color='secondary' sx={{ height: 16, fontSize: '0.6em' }} />
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' color='text.secondary'>{m.email || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <MemberRoleSelector
                            selected={m.roles || []}
                            onChange={(next) => handleRoleChange(m, next)}
                            availableRoles={roles}
                            isOwner={isOwner}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <GlassIconButton
                            size='small'
                            color='error'
                            label='Remove member'
                            disabled={isOwner && ownerCount <= 1}
                            // The last-owner rule is invisible otherwise: the
                            // button just greys out and the user is left
                            // guessing which of the two is the problem.
                            disabledReason={
                              isOwner && ownerCount <= 1
                                ? 'This is the only owner — make someone else an owner before removing them.'
                                : ''
                            }
                            onClick={() => handleRemove(m)}
                          >
                            <DeleteIcon fontSize='small' />
                          </GlassIconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </LoadingOverlay>
        )}
      </PagePaper>

      {selectedBrand && invites.length > 0 && (
        <Box mt='16px'>
          <PagePaper>
            <Box mb='16px'>
              <Typography variant='h6' fontWeight='700'>Pending invitations</Typography>
              <Typography variant='body2' color='text.secondary'>
                People who haven't accepted yet. They'll join the brand automatically once they sign up with the invited email.
              </Typography>
            </Box>
            <TableContainer
              component={Box}
              sx={{
                border: '1px solid', borderColor: 'divider',
                borderRadius: '12px', overflowX: 'auto',
              }}
            >
              <Table size='medium'>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invites.map((inv) => (
                    <TableRow key={inv._id}>
                      <TableCell><Typography variant='body2'>{inv.email}</Typography></TableCell>
                      <TableCell>
                        <Stack direction='row' spacing='4px' flexWrap='wrap' useFlexGap>
                          {(inv.roles || []).map((r) => (
                            <Chip key={r} size='small' label={r} color='primary' />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant='caption' color='text.secondary'>
                          {inv.expires_at ? new Date(inv.expires_at).toLocaleString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        <Stack direction='row' spacing='6px' justifyContent='flex-end'>
                          <Button size='small' variant='outlined' onClick={() => handleCopyInviteLink(inv)}>
                            Copy link
                          </Button>
                          <GlassIconButton
                            size='small'
                            color='error'
                            label='Revoke invite'
                            onClick={() => handleRevokeInvite(inv)}
                          >
                            <DeleteIcon fontSize='small' />
                          </GlassIconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PagePaper>
        </Box>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        availableRoles={roles}
        onSubmit={handleInvite}
        isSubmitting={inviting}
      />
    </Box>
  );
}
